import { useState } from 'react'
import { itemFor, type PerkBenefit, type PerkCard, type PerkUnmatchedCredit, type PerksState } from '@/lib/perks'
import {
  AddCardDialog,
  AssignCreditDialog,
  BenefitDialog,
  BenefitFormDialog,
  CardFormDialog,
  EnrollmentDialog,
  type CardFormTarget,
} from './PerkDialogs'

/** Every perks dialog, shared by the overview and card pages. */
export function usePerkDialogs(state: PerksState | undefined, onCardAdded?: (cardId: number) => void) {
  const [benefitId, setBenefitId] = useState<number | null>(null)
  const [benefitForm, setBenefitForm] = useState<{ card: PerkCard; benefit: PerkBenefit | null } | null>(null)
  const [cardForm, setCardForm] = useState<CardFormTarget | null>(null)
  const [picker, setPicker] = useState(false)
  const [credit, setCredit] = useState<PerkUnmatchedCredit | null>(null)
  const [enroll, setEnroll] = useState(false)

  // Recomputed from the latest state so the dialog updates after each change.
  let item = null
  if (state && benefitId !== null) {
    for (const card of state.cards) {
      const benefit = card.benefits.find((b) => b.id === benefitId)
      if (benefit) item = itemFor(state, card, benefit, new Date())
    }
  }

  const dialogs = (
    <>
      <BenefitDialog
        item={item}
        onOpenChange={(open) => !open && setBenefitId(null)}
        onEdit={(card, benefit) => {
          setBenefitId(null)
          setBenefitForm({ card, benefit })
        }}
      />
      <BenefitFormDialog target={benefitForm} onOpenChange={(open) => !open && setBenefitForm(null)} />
      <AddCardDialog open={picker} state={state} onOpenChange={setPicker} onPick={setCardForm} />
      <CardFormDialog
        target={cardForm}
        state={state}
        onOpenChange={(open) => !open && setCardForm(null)}
        onSaved={(next) => {
          if (cardForm?.card) return
          const added = next.cards.reduce<PerkCard | null>((max, c) => (!max || c.id > max.id ? c : max), null)
          if (added) onCardAdded?.(added.id)
        }}
      />
      <AssignCreditDialog credit={credit} state={state} onOpenChange={(open) => !open && setCredit(null)} />
      <EnrollmentDialog open={enroll} state={state} onOpenChange={setEnroll} />
    </>
  )

  return {
    dialogs,
    openBenefit: (id: number) => setBenefitId(id),
    addBenefit: (card: PerkCard) => setBenefitForm({ card, benefit: null }),
    addCard: () => setPicker(true),
    addCardFrom: (target: CardFormTarget) => setCardForm(target),
    editCard: (card: PerkCard) => setCardForm({ card }),
    assignCredit: setCredit,
    openEnrollment: () => setEnroll(true),
  }
}
